import type {Config} from '@/api/pageService';
import type {
  BlockDeviceMapping,
  Image,
  BlocksItem,
  RegionAllOf,
  CategoryAllOf,
  HotelAllOf,
} from '@/api/pageService/client';
import type {Breakpoint} from '@travel-frontend/themes';

import {createDirectusAssetUrl} from '@/utils/createDirectusAssetUrl';

export const mapBreakpointToDeviceId = (
  breakpoint: Breakpoint
): keyof BlockDeviceMapping => {
  switch (breakpoint) {
    case 'xl':
    case 'lg': {
      return 'lg';
    }
    case 'md': {
      return 'md';
    }
    default: {
      return 'sm';
    }
  }
};

export const resolveImageUrl = (
  image?: Image,
  size: 'large' | 'small' = 'large'
): string => {
  if (!image) {
    return '';
  }

  if (image.url) {
    return image.url;
  }

  if (image.type === 'directus' && image.id && image.extension) {
    return createDirectusAssetUrl(`${image.id}.${image.extension}`);
  }

  if (image.type === 'external') {
    return (
      image.url
        ?.replace('{width}', size === 'large' ? '700' : '400')
        .replace('{height}', size === 'large' ? '525' : '300') ?? ''
    );
  }

  return '';
};

export const findContentBlock = ({
  deviceId,
  collectionId,
  config,
  section,
}: {
  deviceId: keyof BlockDeviceMapping;
  collectionId: string;
  config: Config | null;
  section: keyof RegionAllOf | keyof CategoryAllOf | keyof HotelAllOf;
}): BlocksItem | undefined => {
  if (!config) {
    return;
  }
  // @ts-expect-error ...
  const blockIds = config[section]?.[deviceId]?.map(({id}: {id: string}) => id);

  if (blockIds.length === 0) {
    return;
  }
  const contentBlock = config.blocks.find(
    (block) => blockIds.includes(block.id) && block.collection === collectionId
  );

  return contentBlock;
};

export const findContentBlockItem = <T>({
  deviceId,
  collectionId,
  config,
  section,
}: {
  deviceId: keyof BlockDeviceMapping;
  collectionId: string;
  config: Config | null;
  section: keyof RegionAllOf | keyof CategoryAllOf | keyof HotelAllOf;
}) => {
  return findContentBlock({deviceId, collectionId, config, section})?.item as
    | T
    | undefined;
};
